import { Button } from "@/components/button/Button";
import { Checkbox } from "@/components/checkbox/Checkbox";
import { Input } from "@/components/input/Input";
import type { EmailSignInForm } from "./EmailSignIn.interfaces";
import { emailPattern, FORM_FIELDS } from "@/constants/emailForm.constant";
import { useAuthentication } from "@/context/Authentication";
import classNames from "classnames";
import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import type { FC } from "react";
import { useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styles from "./EmailSignIn.module.scss";
import type { EmailAuthError } from "@/services/email-auth-service";
import { emailAuthService } from "@/services/email-auth-service";
import { remoteLogger } from "@/utils/remoteLogger";
import { SnackbarContext } from "@/components/snackbar/Snackbar.context";
import {
  DEFAULT_NOTIFICATION_TIMEOUT_MS,
  SeverityType,
} from "@/components/snackbar/Snackbar";
import { PROFILE_FOLLOWING_ROUTE } from "@/constants/routes.constant";
import type { TrackingEvent } from "@/modules/analytics";
import { RudderstackEventName, trackingService } from "@/modules/analytics";
import { useLoginRedirectProps } from "@/utils/hooks/useLoginRedirectProps";

const EmailSignIn: FC = () => {
  const methods = useForm<EmailSignInForm>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      [FORM_FIELDS.PASSWORD]: "",
      [FORM_FIELDS.EMAIL]: "",
    },
    shouldFocusError: true,
  });
  const { t } = useTranslation("web-payments");
  const [passwordView, setPasswordView] = useState(false);
  const showNotification = useContext(SnackbarContext);
  const { startSignInViaEmail } = useAuthentication();
  const { origin, redirectUrl } = useLoginRedirectProps(
    PROFILE_FOLLOWING_ROUTE,
  );

  function handleFailure(err: unknown) {
    remoteLogger.error(err);
    const errorType = emailAuthService.parseError(err);

    const errorTypeToMessage: Readonly<Record<EmailAuthError, string>> = {
      "email-already-in-use": t`LOGIN_FAILED_EMAIL_ALREADY_IN_USE`,
      "user-not-found": t`LOGIN_FAILED_USER_NOT_FOUND`,
      "wrong-password": t`LOGIN_FAILED_EMAIL_WRONG_PASSWORD`,
      unknown: t`LOGIN_FAILED`,
    };

    showNotification?.({
      message: errorTypeToMessage[errorType],
      type: SeverityType.error,
      dismissible: true,
      autoHideDuration: DEFAULT_NOTIFICATION_TIMEOUT_MS,
    });
  }

  async function handleSubmitData({ email, password }: EmailSignInForm) {
    try {
      const user = await emailAuthService.signIn({ email, password });

      startSignInViaEmail({
        token: user.token,
        redirectUrl,
        origin,
      });
    } catch (e) {
      handleFailure(e);
    }
  }

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={styles.form}
          onSubmit={(e) => void methods.handleSubmit(handleSubmitData)(e)}
        >
          <div className={styles.inputWrapper}>
            <Input
              type="email"
              autoComplete="on"
              label={t`LOGIN_EMAIL`}
              {...methods.register(FORM_FIELDS.EMAIL, {
                required: true,
                pattern: emailPattern,
              })}
              error={methods.formState.errors[FORM_FIELDS.EMAIL]}
            />
          </div>

          <div className={styles.inputWrapper}>
            <Input
              label={t`LOGIN_PASSWORD`}
              type={passwordView ? "text" : "password"}
              autoComplete="on"
              {...methods.register(FORM_FIELDS.PASSWORD, {
                required: true,
                minLength: 1,
                maxLength: 258,
              })}
              error={methods.formState.errors[FORM_FIELDS.PASSWORD]}
            />
          </div>
          <div className={styles.forgotPassword}>
            <Link
              href="/payments/forgot-password"
              className={styles.forgotPasswordText}
            >
              {t`LOGIN_FORGOT_PASSWORD`}
            </Link>
          </div>

          <label
            className={classNames(
              styles.checkBoxLabel,
              styles.showPasswordInput,
            )}
          >
            <Checkbox
              onChange={() => {
                setPasswordView(!passwordView);
              }}
            />
            <p className={styles.checkBoxLabelText}>{t`SHOW_PASSWORD`}</p>
          </label>

          <Button
            variant="fill"
            type="submit"
            disabled={
              !methods.formState.isDirty ||
              methods.formState.isSubmitting ||
              !methods.formState.isValid
            }
            onClick={() => {
              const trackingEvent: TrackingEvent = {
                params: {
                  login_origin: origin,
                  login_source: redirectUrl,
                },
                trackers: [
                  {
                    kind: "rudderstack",
                    extraParams: { name: RudderstackEventName.LoginClicked },
                  },
                ],
              };

              trackingService.sendEvent(trackingEvent);
            }}
            className={styles.submitButton}
          >
            <span>{t`LOGIN_CTA_SIGN_IN`}</span>
          </Button>
        </form>
      </FormProvider>
    </>
  );
};

export { EmailSignIn };
